/**
 * MarkdownWithCitations — citation-aware markdown renderer used by the
 * scoped chat threads (per-issue, per-clause, per-finding). Mirrors the
 * assistant-message inline `[N]` → InlineCitation injection that ChatMessage
 * uses for the general chat (Plan 159 / Issue #700 / Issue #761), but without
 * ChatMessage's bubble chrome or typography overrides. Styling is deferred
 * to the parent (e.g. `desk-markdown`) so this fits inside the scoped-thread
 * bubble layouts without restyling them.
 */
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import InlineCitation from './InlineCitation';
import type { Citation } from './types';

interface MarkdownWithCitationsProps {
  content: string;
  citations: Citation[];
  onCitationClick?: (citation: Citation) => void;
}

const MarkdownWithCitations: React.FC<MarkdownWithCitationsProps> = ({
  content,
  citations,
  onCitationClick,
}) => {
  const hasMarkers = !!content.match(/\[\d+\]/);
  if (citations.length === 0 || !hasMarkers) {
    return <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>;
  }

  // Build a lookup keyed by the numeric citation id surfaced in `[N]`.
  // Pre-Plan-159 persisted matter-wide rows always carry a numeric `id`; the
  // dual-section playbook citations (Plan 150 §7.3) live elsewhere
  // (structured_metadata.citations_guidance) and do not flow through this
  // renderer. The runtime-type guard matches ChatMessage's compat behaviour.
  const citationMap = new Map<number, Citation>(
    citations
      .filter((c) => typeof (c as { id?: unknown }).id === 'number')
      .map((c) => [c.id as number, c]),
  );
  let occurrenceIdx = 0;

  const injectCitations = (children: React.ReactNode): React.ReactNode => {
    return React.Children.map(children, (child) => {
      if (typeof child === 'string' && child.match(/\[\d+\]/)) {
        const parts: React.ReactNode[] = [];
        let lastIdx = 0;
        const pattern = /\[(\d+)\]/g;
        let m: RegExpExecArray | null;
        while ((m = pattern.exec(child)) !== null) {
          if (m.index > lastIdx) parts.push(child.substring(lastIdx, m.index));
          const num = parseInt(m[1], 10);
          const cit = citationMap.get(num);
          if (cit) {
            parts.push(
              <InlineCitation
                key={`cite-${occurrenceIdx}-${num}`}
                citationNumber={num}
                citation={cit}
                onClick={onCitationClick}
              />,
            );
          } else {
            parts.push(
              <span key={`cite-missing-${occurrenceIdx}-${num}`}>[{num}]</span>,
            );
          }
          occurrenceIdx++;
          lastIdx = m.index + m[0].length;
        }
        if (lastIdx < child.length) parts.push(child.substring(lastIdx));
        return (
          <React.Fragment key={`cite-text-${occurrenceIdx}`}>{parts}</React.Fragment>
        );
      }
      if (
        React.isValidElement(child) &&
        (child.props as { children?: React.ReactNode }).children
      ) {
        return React.cloneElement(
          child,
          {},
          injectCitations(
            (child.props as { children?: React.ReactNode }).children,
          ),
        );
      }
      return child;
    });
  };

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => <p>{injectCitations(children)}</p>,
        h1: ({ children }) => <h1>{injectCitations(children)}</h1>,
        h2: ({ children }) => <h2>{injectCitations(children)}</h2>,
        h3: ({ children }) => <h3>{injectCitations(children)}</h3>,
        li: ({ children }) => <li>{injectCitations(children)}</li>,
        td: ({ children }) => <td>{injectCitations(children)}</td>,
        th: ({ children }) => <th>{injectCitations(children)}</th>,
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

export default MarkdownWithCitations;
